import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import { Icon } from "semantic-ui-react";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";

// ── Helpers ───────────────────────────────────────────────────
const fmt   = n => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const parse = s => parseFloat(String(s).replace(/[,\s₹]/g, "")) || 0;

const getFormattedDate = () => new Date().toISOString().slice(0, 19).replace("T", " ");

function sumChildren(nodes) { return nodes.reduce((s, n) => s + (n.amount || 0), 0); }

// ── Design tokens ─────────────────────────────────────────────
const FONT       = "'Segoe UI', system-ui, sans-serif";
const NAVY       = "#1E3A5F";
const BORDER     = "#d0d5dd";
const BG_INPUT   = "#f0f2f5";
const BG_PAGE    = "#e8eaf0";
const BG_HDR_COL = "#e8eaee";
const TEXT_MAIN  = "#222";
const TEXT_LABEL = NAVY;
const TEXT_META  = "#666";
const RED        = "#c0392b";
const GREEN      = "#1a7a4a";
const ORANGE     = "#b45309";

// ── Sizing ────────────────────────────────────────────────────
const INPUT_H = 34;
const ROW_PY  = "7px";
const ROW_PX  = "12px";
const INDENT  = "48px";
const F_BASE  = 13;
const F_SM    = 12;
const F_HDR   = 14;
const F_COL   = 12;

const inputBase = {
  fontFamily: FONT, fontSize: F_BASE, color: TEXT_MAIN,
  background: BG_INPUT, border: `1px solid ${BORDER}`,
  borderRadius: 4, height: INPUT_H, width: "100%",
  padding: "0 9px", outline: "none", boxSizing: "border-box",
};

const colHdr = {
  fontFamily: FONT, fontSize: F_COL, fontWeight: 700,
  color: "#333", textTransform: "uppercase", letterSpacing: "0.05em",
  display: "flex", alignItems: "center",
};

const CAT_COLS = "28px 1fr 120px 120px 160px";
const SUB_COLS = "1fr 130px 130px 120px 120px 160px";

// ── Status badge ──────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    draft:    { bg: "#f0f2f5",  color: "#555",   label: "Draft"    },
    approved: { bg: "#d1fae5",  color: "#065f46", label: "Approved" },
    hold:     { bg: "#fef3c7",  color: "#92400e", label: "On Hold"  },
    rejected: { bg: "#fee2e2",  color: "#991b1b", label: "Rejected" },
  };
  const s = map[status?.toLowerCase()] || map.draft;
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 12,
      background: s.bg, color: s.color,
      fontFamily: FONT, fontSize: F_SM, fontWeight: 600,
    }}>{s.label}</span>
  );
}

// ── Read-only Amount display ──────────────────────────────────
function AmtDisplay({ value, bold, color }) {
  return (
    <div style={{
      ...inputBase,
      display: "flex", alignItems: "center", justifyContent: "flex-end",
      gap: 4, background: "#f8f9fb", cursor: "default",
      fontWeight: bold ? 600 : 400,
      color: color || TEXT_MAIN,
      fontVariantNumeric: "tabular-nums",
    }}>
      <span style={{ color: TEXT_META, fontSize: F_SM }}>₹</span>
      <span>{fmt(value)}</span>
    </div>
  );
}

// ── Read-only field ───────────────────────────────────────────
function ROField({ value, placeholder }) {
  return (
    <div style={{
      ...inputBase,
      display: "flex", alignItems: "center",
      background: "#f8f9fb", cursor: "default",
      color: value ? TEXT_MAIN : TEXT_META,
    }}>
      {value || placeholder || "—"}
    </div>
  );
}

// ── Remarks modal ─────────────────────────────────────────────
function RemarksModal({ action, onConfirm, onCancel }) {
  const [remarks, setRemarks] = useState("");
  const isApprove = action === "approved";

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 10, padding: "28px 32px",
        minWidth: 420, maxWidth: 520, width: "90%",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        fontFamily: FONT,
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 6 }}>
          {isApprove ? "✅ Approve Budget" : "⏸ Hold Budget"}
        </div>
        <div style={{ fontSize: F_SM, color: TEXT_META, marginBottom: 16 }}>
          {isApprove
            ? "This will mark the budget as Approved. Add optional remarks below."
            : "This will place the budget On Hold. Please provide a reason."}
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ fontSize: F_BASE, fontWeight: 600, color: TEXT_LABEL, display: "block", marginBottom: 5 }}>
            Remarks {!isApprove && <span style={{ color: RED }}>*</span>}
          </label>
          <textarea
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            rows={4}
            placeholder={isApprove ? "Optional remarks…" : "Reason for hold (required)…"}
            style={{
              ...inputBase, height: "auto", resize: "vertical",
              padding: "8px 9px", lineHeight: 1.5,
            }}
          />
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{
            padding: "8px 22px", borderRadius: 5,
            border: `1px solid ${BORDER}`, background: "#f5f5f5",
            fontFamily: FONT, fontSize: F_BASE, cursor: "pointer", color: TEXT_MAIN,
          }}>Cancel</button>
          <button
            onClick={() => {
              if (!isApprove && !remarks.trim()) {
                alert("Please provide a reason for placing the budget on hold.");
                return;
              }
              onConfirm(remarks.trim());
            }}
            style={{
              padding: "8px 26px", borderRadius: 5, border: "none",
              background: isApprove ? GREEN : ORANGE,
              color: "#fff", fontFamily: FONT, fontSize: F_BASE,
              fontWeight: 600, cursor: "pointer",
            }}
          >
            {isApprove ? "Approve" : "Hold"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sub row (read-only) ───────────────────────────────────────
function SubRowView({ node }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: SUB_COLS, gap: 6,
      alignItems: "center",
      padding: `${ROW_PY} ${ROW_PX} ${ROW_PY} ${INDENT}`,
      borderTop: `1px solid ${BORDER}`,
      background: "#fff",
    }}>
      <ROField value={node.name} placeholder="Sub-category" />
      <ROField value={node.expCodeLabel} placeholder="Exp. Code" />
      <ROField value={node.costCodeLabel} placeholder="Cost Code" />
      <ROField value={node.fromDate} placeholder="—" />
      <ROField value={node.toDate}   placeholder="—" />
      <AmtDisplay value={node.amount} />
    </div>
  );
}

// ── Category row (read-only, collapsible) ─────────────────────
function CategoryRowView({ node, catIndex, totalBudget, onChange }) {
  const topPct = totalBudget > 0 ? Math.round((node.amount / totalBudget) * 100) : 0;

  return (
    <div style={{ borderTop: catIndex > 0 ? `1px solid ${BORDER}` : "none" }}>
      <div style={{
        display: "grid", gridTemplateColumns: CAT_COLS, gap: 6,
        alignItems: "center", padding: `${ROW_PY} ${ROW_PX}`,
        borderLeft: `3px solid ${NAVY}`, background: "#fff",
      }}>
        <button
          onClick={() => onChange(node._localId)}
          style={{
            width: 24, height: 24, borderRadius: 3,
            border: `1px solid ${BORDER}`, background: "#fff",
            cursor: "pointer", fontSize: 9, color: NAVY, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 0, flexShrink: 0, fontFamily: FONT,
          }}
        >{node.open ? "▲" : "▼"}</button>

        <div style={{ ...inputBase, fontWeight: 600, background: "#f8f9fb", display: "flex", alignItems: "center" }}>
          {node.name}
        </div>
        <ROField value={node.fromDate} placeholder="—" />
        <ROField value={node.toDate}   placeholder="—" />
        <AmtDisplay value={node.amount} bold color={NAVY} />
      </div>

      {node.open && (
        <>
          {node.children.length > 0 && (
            <div style={{
              display: "grid", gridTemplateColumns: SUB_COLS, gap: 6,
              padding: `8px ${ROW_PX} 8px ${INDENT}`,
              background: BG_HDR_COL,
              borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`,
            }}>
              {["Sub-category", "Exp. Code", "Cost Code", "From", "To", "Amount"].map((h, i) => (
                <div key={i} style={colHdr}>{h}</div>
              ))}
            </div>
          )}
          {node.children.length === 0 && (
            <div style={{ fontFamily: FONT, fontSize: F_BASE, color: TEXT_META, fontStyle: "italic", padding: `8px ${ROW_PX} 8px ${INDENT}`, background: "#fff" }}>
              No sub-categories.
            </div>
          )}
          {node.children.map(child => (
            <SubRowView key={child._localId} node={child} />
          ))}
        </>
      )}
    </div>
  );
}

// ── Budget Detail View ────────────────────────────────────────
function BudgetDetailView({ budgetId, onBack }) {
  const [userCredentials] = useState(JSON.parse(sessionStorage.getItem("userCredentials")));
  const [header,  setHeader]  = useState(null);
  const [nodes,   setNodes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // "approved" | "hold" | null
  const [saving,  setSaving]  = useState(false);

  let _lid = 2000;
  const nextLid = () => ++_lid;

  useEffect(() => {
    const load = async () => {
      try {
        const creds = Fetchexecutequery();
        const run   = async q => { const r = await axios.post(creds[1], { query: q }, creds[0]); return r.data || []; };

        const [headers, cats, subs, expCodes, costCodes] = await Promise.all([
          run(`SELECT bh.*, e.name AS entity_name, COALESCE(d.name,'') AS division_name
               FROM budget.budget_header bh
               LEFT JOIN user_management.entities  e ON e.entity_id   = bh.entity_id
               LEFT JOIN user_management.divisions d ON d.division_id = bh.division_id
               WHERE bh.id = ${budgetId}`),
          run(`SELECT * FROM budget.budget_category    WHERE budget_header_id = ${budgetId} AND is_active = 1 ORDER BY sort_order`),
          run(`SELECT * FROM budget.budget_subcategory WHERE budget_header_id = ${budgetId} AND is_active = 1 ORDER BY sort_order`),
          run(`SELECT * FROM payments.expense_categories   WHERE is_Active = 1`),
          run(`SELECT * FROM payments.cost_center_details  WHERE is_Active = 1`),
        ]);

        const expMap  = Object.fromEntries(expCodes.map(r  => [String(r.id),  r.expense_code]));
        const costMap = Object.fromEntries(costCodes.map(r => [String(r.id),  r.cost_center]));

        if (headers.length) setHeader(headers[0]);

        const built = cats.map(cat => ({
          _localId: nextLid(), id: cat.id,
          name: cat.category_name,
          amount: Number(cat.amount),
          fromDate: cat.from_date ? cat.from_date.slice(0, 10) : "",
          toDate:   cat.to_date   ? cat.to_date.slice(0, 10)   : "",
          open: true,
          children: subs
            .filter(s => s.budget_category_id === cat.id)
            .map(s => ({
              _localId: nextLid(), id: s.id,
              name: s.subcategory_name,
              amount: Number(s.amount),
              expCodeLabel:  expMap[String(s.expense_code_id)]  || "",
              costCodeLabel: costMap[String(s.cost_code_id)]    || "",
              fromDate: s.from_date ? s.from_date.slice(0, 10) : "",
              toDate:   s.to_date   ? s.to_date.slice(0, 10)   : "",
            })),
        }));
        setNodes(built);
      } catch (err) {
        console.error("Load error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [budgetId]);

  const toggleOpen = useCallback(lid => {
    setNodes(p => p.map(n => n._localId === lid ? { ...n, open: !n.open } : n));
  }, []);

  const handleAction = async (action, remarks) => {
    setSaving(true);
    try {
      const now = getFormattedDate();
      const uc  = Fetchupdatequery();
      await axios.post(uc[1], {
        schema_name: "budget", table: "budget_header",
        set_column: {
          status: action,
          comments: remarks || null,
          approved_by:   userCredentials.user_id,
          approved_on:   now,
          last_updated_on:  now,
          last_updated_by:  userCredentials.user_name,
        },
        where: [{ id: budgetId }],
      }, uc[0]);

      setHeader(h => ({ ...h, status: action, remarks }));
      setModal(null);
      alert(action === "approved" ? "Budget approved successfully!" : "Budget placed on hold.");
      onBack();
    } catch (err) {
      console.error("Action error:", err);
      alert("Error updating budget status. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontFamily: FONT, color: TEXT_META }}>
        Loading budget details…
      </div>
    );
  }
  if (!header) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontFamily: FONT, color: RED }}>
        Budget not found.
      </div>
    );
  }

  const topSum    = sumChildren(nodes);
  const totalCats = nodes.length;
  const totalSubs = nodes.reduce((a, n) => a + n.children.length, 0);
  const pctFill   = header.total_budget > 0 ? Math.min((topSum / header.total_budget) * 100, 100) : 0;
  const alreadyActioned = ["approved", "hold"].includes(header.status?.toLowerCase());

  const card        = { background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 8, marginBottom: 14, overflow: "hidden" };
  const bannerTitle = { fontFamily: FONT, fontSize: F_HDR, fontWeight: 700, color: NAVY };

  return (
    <div style={{ fontFamily: FONT, background: BG_PAGE, color: TEXT_MAIN, fontSize: F_BASE, padding: "0 16px 40px" }}>

      {/* Modals */}
      {modal && (
        <RemarksModal
          action={modal}
          onConfirm={remarks => handleAction(modal, remarks)}
          onCancel={() => setModal(null)}
        />
      )}

      {/* Back + action bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0 6px" }}>
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 6,
          background: "transparent", border: "none",
          fontFamily: FONT, fontSize: F_BASE, color: NAVY,
          fontWeight: 600, cursor: "pointer", padding: 0,
        }}>
          ← Back to list
        </button>

        {!alreadyActioned && (
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setModal("hold")} disabled={saving} style={{
              padding: "8px 24px", borderRadius: 5,
              border: `1.5px solid ${ORANGE}`, background: "#fffbeb",
              color: ORANGE, fontFamily: FONT, fontSize: F_BASE,
              fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
            }}>
              ⏸ Hold
            </button>
            <button onClick={() => setModal("approved")} disabled={saving} style={{
              padding: "8px 24px", borderRadius: 5,
              border: "none", background: GREEN,
              color: "#fff", fontFamily: FONT, fontSize: F_BASE,
              fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
            }}>
              ✅ Approve
            </button>
          </div>
        )}

        {alreadyActioned && (
          <StatusBadge status={header.status} />
        )}
      </div>

      {/* Budget Information (read-only) */}
      <div style={{ ...card, marginTop: 4 }}>
        <div style={{ padding: "10px 16px 8px", background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BORDER}` }}>
          <span style={bannerTitle}>Budget Information</span>
          <StatusBadge status={header.status} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, padding: "14px 16px", borderBottom: `1px solid ${BORDER}` }}>
          <div>
            <div style={{ fontFamily: FONT, fontSize: F_BASE, fontWeight: 600, color: TEXT_LABEL, marginBottom: 5 }}>Financial Year:</div>
            <ROField value={header.financial_year} />
          </div>
          <div>
            <div style={{ fontFamily: FONT, fontSize: F_BASE, fontWeight: 600, color: TEXT_LABEL, marginBottom: 5 }}>Entity:</div>
            <ROField value={header.entity_name} />
          </div>
          <div>
            <div style={{ fontFamily: FONT, fontSize: F_BASE, fontWeight: 600, color: TEXT_LABEL, marginBottom: 5 }}>Division:</div>
            <ROField value={header.division_name} placeholder="—" />
          </div>
          <div>
            <div style={{ fontFamily: FONT, fontSize: F_BASE, fontWeight: 600, color: TEXT_LABEL, marginBottom: 5 }}>Total Budget:</div>
            <AmtDisplay value={header.total_budget} bold color={NAVY} />
          </div>
          {/* Allocated / Unallocated */}
          <div style={{ padding: "8px 9px", background: "#f9f9f9", border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: F_SM, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontWeight: 600, color: TEXT_LABEL }}>Allocated:</span>
              <span style={{ fontWeight: 700, color: NAVY }}>₹{fmt(topSum)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 600, color: TEXT_LABEL }}>Unallocated:</span>
              <span style={{ fontWeight: 700, color: topSum > header.total_budget ? RED : GREEN }}>
                ₹{fmt(Math.abs(header.total_budget - topSum))}
              </span>
            </div>
          </div>
        </div>

        {/* Remarks row — shown if remarks exist */}
        {header.remarks && (
          <div style={{ padding: "10px 16px", background: "#fffbeb", borderTop: `1px solid #fde68a`, display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ fontSize: F_SM, fontWeight: 600, color: ORANGE, whiteSpace: "nowrap" }}>Remarks:</span>
            <span style={{ fontSize: F_SM, color: TEXT_MAIN }}>{header.remarks}</span>
          </div>
        )}
      </div>

      {/* Categories (read-only) */}
      <div style={card}>
        <div style={{ padding: "12px 16px", background: "#fff", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={bannerTitle}>Categories &amp; Sub-categories</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div style={{ fontSize: F_SM, fontWeight: 600, color: TEXT_LABEL, marginBottom: 2 }}>Summary</div>
              <div style={{ fontSize: F_BASE, fontWeight: 700, color: NAVY }}>{totalCats} categories · {totalSubs} sub-items</div>
            </div>
            <div>
              <div style={{ fontSize: F_SM, fontWeight: 600, color: TEXT_LABEL, marginBottom: 2 }}>Budget Utilization</div>
              <div style={{ fontSize: F_BASE, fontWeight: 700, color: NAVY }}>₹{fmt(topSum)} allocated · {Math.round(pctFill)}% utilized</div>
            </div>
          </div>
        </div>

        {/* Category column header */}
        {nodes.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: CAT_COLS, gap: 6,
            padding: `8px ${ROW_PX}`, background: BG_HDR_COL, borderBottom: `1px solid ${BORDER}`,
          }}>
            <div />
            {["Category", "From", "To", "Budget"].map((h, i) => (
              <div key={i} style={colHdr}>{h}</div>
            ))}
          </div>
        )}

        {nodes.length === 0 && (
          <div style={{ padding: "28px 0", textAlign: "center", fontFamily: FONT, fontSize: F_BASE, color: TEXT_META }}>
            No categories found.
          </div>
        )}

        {nodes.map((node, i) => (
          <CategoryRowView
            key={node._localId}
            node={node}
            catIndex={i}
            totalBudget={header.total_budget}
            onChange={toggleOpen}
          />
        ))}
      </div>

      {/* Bottom action buttons (duplicate for easy access) */}
      {!alreadyActioned && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
          <button onClick={() => setModal("hold")} disabled={saving} style={{
            padding: "8px 24px", borderRadius: 5,
            border: `1.5px solid ${ORANGE}`, background: "#fffbeb",
            color: ORANGE, fontFamily: FONT, fontSize: F_BASE,
            fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
          }}>
            ⏸ Hold
          </button>
          <button onClick={() => setModal("approved")} disabled={saving} style={{
            padding: "8px 24px", borderRadius: 5,
            border: "none", background: GREEN,
            color: "#fff", fontFamily: FONT, fontSize: F_BASE,
            fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
          }}>
            ✅ Approve
          </button>
        </div>
      )}
    </div>
  );
}

// ── Budget Approval List ──────────────────────────────────────
function BudgetApprovalList({ onView }) {
  const [userCredentials] = useState(JSON.parse(sessionStorage.getItem("userCredentials")));
  const [rowData,    setRowData]    = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [filter,     setFilter]     = useState("");
  const gridRef = useRef(null);

  const formatColumnName = k => k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  useEffect(() => {
    const fetchList = async () => {
      try {
        const creds = Fetchexecutequery();
        const res   = await axios.post(creds[1], {
          query: `
            SELECT
              bh.id,
              bh.financial_year,
              COALESCE(e.name, '')  AS entity_name,
              COALESCE(d.name, '')  AS division_name,
              bh.total_budget,
              bh.status,
              bh.created_on
            FROM budget.budget_header bh
            LEFT JOIN user_management.entities  e ON e.entity_id   = bh.entity_id
            LEFT JOIN user_management.divisions d ON d.division_id = bh.division_id
            WHERE bh.is_active = 1
              AND bh.entity_id = ${userCredentials.selectedentity}
            ORDER BY bh.id DESC
          `,
        }, creds[0]);

        const data = res.data || [];
        if (data.length > 0) {
          const cols = Object.keys(data[0])
            .filter(k => k !== "id")
            .map(k => {
              if (k === "status") {
                return {
                  headerName: "Status", field: "status", sortable: true, filter: true, resizable: true, width: 130,
                  cellRendererFramework: params => <StatusBadge status={params.value} />,
                };
              }
              return {
                headerName: formatColumnName(k), field: k,
                sortable: true, filter: true, resizable: true,
              };
            });

          cols.push({
            headerName: "Action", field: "action", width: 90, sortable: false, filter: false,
            cellRendererFramework: params => (
              <Icon
                name="eye"
                style={{ cursor: "pointer", color: NAVY, fontSize: 16 }}
                onClick={() => onView(params.data.id)}
              />
            ),
          });

          setColumnDefs(cols);
          setRowData(data);
        }
      } catch (err) {
        console.error("Error fetching budget list:", err);
      }
    };
    fetchList();
  }, []);

  return (
    <div style={{ padding: "12px 16px", background: BG_PAGE, minHeight: "100%" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <input
          type="text" placeholder="Search…" value={filter}
          onChange={e => {
            setFilter(e.target.value);
            gridRef.current?.api?.setQuickFilter(e.target.value);
          }}
          style={{ ...inputBase, width: 260 }}
        />
      </div>
      <div className="ag-theme-alpine" style={{ height: 480, width: "100%", borderRadius: 6, overflow: "hidden", border: `1px solid ${BORDER}` }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          pagination={true}
          paginationPageSize={10}
        />
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────
export default function BudgetApproval() {
  const [activeTab,      setActiveTab]      = useState(0);
  const [viewingBudget,  setViewingBudget]  = useState(null);

  const handleView = (id) => { setViewingBudget(id); setActiveTab(0); };
  const handleBack = ()   => { setViewingBudget(null); setActiveTab(1); };

  return (
    <div style={{ fontFamily: FONT }}>
      <h2 className="pt-1 pb-1 text-center" style={{ color: "#4969ab", fontSize: 18 }}>
        Budget Approval
      </h2>

      <Tabs selectedIndex={activeTab} onSelect={setActiveTab}>
        <TabList>
          <Tab>{viewingBudget ? "Budget Detail" : "Approval"}</Tab>
          <Tab>All Budgets</Tab>
        </TabList>

        <TabPanel>
          {viewingBudget ? (
            <BudgetDetailView budgetId={viewingBudget} onBack={handleBack} />
          ) : (
            <div style={{ padding: 40, textAlign: "center", fontFamily: FONT, fontSize: F_BASE, color: TEXT_META }}>
              Select a budget from <strong>All Budgets</strong> tab to review it.
            </div>
          )}
        </TabPanel>

        <TabPanel>
          <BudgetApprovalList onView={handleView} />
        </TabPanel>
      </Tabs>
    </div>
  );
}